import { BarcodeOutlined } from "@ant-design/icons";
import { Image, Modal, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ICashCheckQuantityVarianceLine } from "../../../models/order/cash-check";
import { IProductsOrder } from "../../../models/order/products-order/response";
import {
  formatSignedWeightFromGrams,
  formatWeightFromGrams,
  IWeightUnitLabels,
} from "../../../utils/helpers/weight";
import Tag from "../../general/antd/tag";

interface IProps {
  open: boolean;
  /** Already-translated refusal text from `errors[0]`. */
  message?: string;
  lines: ICashCheckQuantityVarianceLine[];
  /** Order product lines — the display source, joined by `productOrderId`. */
  orderProducts?: IProductsOrder[];
  confirming: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

const formatQuantity = (
  line: ICashCheckQuantityVarianceLine,
  value: number,
  labels: IWeightUnitLabels,
  pieceLabel: string,
  signed = false,
) => {
  if (line.quantityUnit === "GRAM") {
    return signed
      ? formatSignedWeightFromGrams(value, labels)
      : formatWeightFromGrams(value, labels);
  }

  const sign = signed && value > 0 ? "+" : "";
  return `${sign}${value.toLocaleString()} ${pieceLabel}`;
};

const CashCheckQuantityVarianceModal: React.FC<IProps> = ({
  open,
  message,
  lines,
  orderProducts,
  confirming,
  onConfirm,
  onBack,
}) => {
  const { t } = useTranslation();

  const weightUnitLabels = useMemo(
    () => ({
      gram: t("cash_check_unit_gram"),
      kilogram: t("cash_check_unit_kilogram"),
    }),
    [t],
  );

  const productOrderById = useMemo(() => {
    const map = new Map<number, IProductsOrder>();
    (orderProducts ?? []).forEach((productOrder) =>
      map.set(productOrder.id, productOrder),
    );
    return map;
  }, [orderProducts]);

  // the physically present product: the alternative when one was provided
  const getActualProduct = (line: ICashCheckQuantityVarianceLine): any => {
    const joined = productOrderById.get(line.productOrderId);
    if (!joined) return undefined;
    return joined.isAlternative && joined.alternativeProduct
      ? joined.alternativeProduct
      : joined.product;
  };

  const columns: ColumnsType<ICashCheckQuantityVarianceLine> = [
    {
      title: t("image"),
      key: "image",
      width: 70,
      render: (_, line) => {
        const imageUrl = getActualProduct(line)?.image?.url;
        return imageUrl ? (
          <Image
            src={imageUrl}
            width={48}
            height={48}
            style={{ objectFit: "contain" }}
            preview={{ mask: null }}
          />
        ) : (
          <BarcodeOutlined style={{ fontSize: 24, opacity: 0.35 }} />
        );
      },
    },
    {
      title: t("product"),
      key: "product",
      render: (_, line) => (
        <div>
          <Typography.Text strong>
            {getActualProduct(line)?.name || `#${line.productOrderId}`}
          </Typography.Text>
          {productOrderById.get(line.productOrderId)?.isAlternative && (
            <div>
              <Tag title={t("cash_check_alternative")} color="purple" />
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("cash_check_variance_requested"),
      key: "requested",
      align: "center",
      render: (_, line) =>
        formatQuantity(
          line,
          line.requestedQuantity,
          weightUnitLabels,
          t("piece"),
        ),
    },
    {
      title: t("cash_check_variance_scanned"),
      key: "scanned",
      align: "center",
      render: (_, line) => (
        <Typography.Text strong>
          {formatQuantity(
            line,
            line.scannedQuantity,
            weightUnitLabels,
            t("piece"),
          )}
        </Typography.Text>
      ),
    },
    {
      title: t("cash_check_variance_difference"),
      key: "difference",
      align: "center",
      render: (_, line) => (
        <Typography.Text
          type={line.direction === "UNDER" ? "danger" : "success"}
        >
          {formatQuantity(
            line,
            line.difference,
            weightUnitLabels,
            t("piece"),
            true,
          )}
        </Typography.Text>
      ),
    },
    {
      title: t("cash_check_variance_tolerance"),
      key: "tolerance",
      align: "center",
      render: (_, line) =>
        line.quantityUnit === "GRAM"
          ? `±${formatWeightFromGrams(line.tolerance, weightUnitLabels)}`
          : "-",
    },
  ];

  return (
    <Modal
      title={t("cash_check_variance_title")}
      open={open}
      onCancel={onBack}
      onOk={onConfirm}
      okText={t("cash_check_variance_confirm")}
      cancelText={t("back")}
      confirmLoading={confirming}
      cancelButtonProps={{ disabled: confirming }}
      closable={!confirming}
      maskClosable={false}
      width={820}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">
        {message || t("cash_check_variance_description")}
      </Typography.Paragraph>
      <Table
        rowKey="productOrderId"
        columns={columns}
        dataSource={lines}
        pagination={false}
        size="small"
        scroll={{ x: true }}
      />
    </Modal>
  );
};

export default CashCheckQuantityVarianceModal;
